import React from 'react'
import { massCheck, copyAttempt } from '../timesheetUtils';
import { MdInfoOutline } from 'react-icons/md';

const TimesheetsTab = ({teachers, timesheets, schoolList, schoolTab, currentMonth, currentYear, month, year, setOpenTimesheet, setListOfTeachersTimesheetsForThisMonth, setQuickView}) => {

    const normalizeId = (value) => (value == null ? '' : String(value).trim());
    const normalizeName = (value) => (value == null ? '' : String(value).trim().toLowerCase().replace(/\s+/g, ' '));
    const isSameId = (a, b) => normalizeId(a) === normalizeId(b);

    const openUpATimesheet = (teacherId) => {
        let arrayOfTeachersTimesheets = []
        const targetId = normalizeId(teacherId)
        timesheets.forEach((sheet) => {
            if (isSameId(sheet.teacherId, targetId)) {
                arrayOfTeachersTimesheets.push(sheet)
                setOpenTimesheet(sheet)
            }
        })
        setListOfTeachersTimesheetsForThisMonth(arrayOfTeachersTimesheets)
    }

    const findTimesheetFromTeacherId = (teacherId) => {
        let foundTimesheet = timesheets.find((sheet) => isSameId(sheet.teacherId, teacherId))
        setQuickView(foundTimesheet?.timesheetId)
    }

    const checkIfTheyTurnedInTimesheet = (teacherId) => {
        let foundTimesheet = timesheets.find((sheet) => isSameId(sheet.teacherId, teacherId))
        return foundTimesheet ? foundTimesheet.status.charAt(0).toUpperCase() + foundTimesheet.status.slice(1) : 'Missing'
    }

    const checkIfTheyTurnedInMultipleTimesheets = (teacherId) => {
        let listOfTimesheetsObject = {approved: 0, submitted: 0}
        timesheets.forEach((sheet) => {
            if(isSameId(sheet.teacherId, teacherId)) {
                sheet.status === 'Approved' ? listOfTimesheetsObject = {approved: ++listOfTimesheetsObject.approved, submitted: ++listOfTimesheetsObject.submitted} : listOfTimesheetsObject.submitted = ++listOfTimesheetsObject.submitted   
            }
        })
        return listOfTimesheetsObject
    }

    const ifTheyHaveTurnedInTheirTimesheetCalculatePay = (teacherId) => {
        let foundSheets = []
        timesheets.forEach((sheet) => isSameId(sheet.teacherId, teacherId) && foundSheets.push(sheet))
        return foundSheets.length > 0 ? calculateEstimatedPay(foundSheets) : '-' 
    }

    const calculateEstimatedPay = (arrayOfSheets) => {
        let total = 0
        let minutesTaught = 0
        let allExtras = 0
        for(let i = 0; i < arrayOfSheets.length; i++) {
            let theTimesheet = arrayOfSheets[i]
            for(let i = 0; i < theTimesheet.families.length; i++) {
                let fam = theTimesheet.families[i]
                for(let i = 0; i < fam.lessons.length; i++) {
                    minutesTaught = Number(minutesTaught) + Number(fam.lessons[i].lessonLength)
                }
                for(let i = 0; i < fam.cancellations.length; i++) {
                    if(fam.cancellations[i].cancellor === 'student less than 24hrs') {
                        minutesTaught = Number(minutesTaught) + Number(fam.cancellations[i].lessonLength)
                    }
                }
                for(let i = 0; i < fam.extras.length; i++) {
                    allExtras = Number(allExtras) + Number(fam.extras[i].amount)
                }
            }
        }
        return `$${((minutesTaught/60) * Number(arrayOfSheets[0].teacherRate)) + allExtras}`
    }

    const collectStudentLessonStats = (families = [], teacherId = null) => {
        const studentMap = new Map();
        const nameLookup = new Map();
        const normalizedTeacherId = normalizeId(teacherId);

        const ensureEntry = (rawId, rawName) => {
            const normId = normalizeId(rawId);
            const normName = normalizeName(rawName);

            let entry = null;

            if (normId && studentMap.has(`id::${normId}`)) {
                entry = studentMap.get(`id::${normId}`);
            }
            if (!entry && normName && nameLookup.has(normName)) {
                entry = studentMap.get(nameLookup.get(normName));
            }

            let key = null;
            if (!entry) {
                key = normId ? `id::${normId}` : (normName ? `name::${normName}` : null);
                if (!key) return null;
                entry = studentMap.get(key);
                if (!entry) {
                    entry = { primaryKey: key, expected: 4, actual: 0, names: new Set(), ids: new Set() };
                    studentMap.set(key, entry);
                }
            }

            if (normId) {
                entry.ids.add(normId);
                const idKey = `id::${normId}`;
                studentMap.set(idKey, entry);
                if (!entry.primaryKey.startsWith('id::')) {
                    entry.primaryKey = idKey;
                }
            }
            if (normName) {
                entry.names.add(normName);
                nameLookup.set(normName, entry.primaryKey);
            }
            return entry;
        };

        families.forEach((fam = {}) => {
            const candidateLists = [
                Array.isArray(fam.listOfFamilyStudents) ? fam.listOfFamilyStudents : null,
                Array.isArray(fam.students) ? fam.students : null
            ].filter(Boolean);
            candidateLists.forEach((list) => {
                list.forEach((stu = {}) => {
                    const status = normalizeName(stu.studentStatus);
                    if (status === 'inactive') return;
                    const stuTeacherId = normalizeId(stu.teachersId || stu.teacherId || stu.teacherID);
                    if (normalizedTeacherId && stuTeacherId && stuTeacherId !== normalizedTeacherId) return;
                    const entry = ensureEntry(stu.studentId || stu.studentID, stu.studentName || stu.name);
                    if (entry) {
                        entry.expected = 4;
                    }
                });
            });
        });

        families.forEach((fam = {}) => {
            const lessons = Array.isArray(fam.lessons) ? fam.lessons : [];
            lessons.forEach((lesson = {}) => {
                const entry = ensureEntry(lesson.studentId || lesson.studentID, lesson.studentName || lesson.student || lesson.name);
                if (entry) entry.actual += 1;
            });

            const cancellations = Array.isArray(fam.cancellations) ? fam.cancellations : [];
            cancellations.forEach((cancellation = {}) => {
                if ((cancellation.cancellor || '').toLowerCase() !== 'student less than 24hrs') return;
                const entry = ensureEntry(cancellation.studentId || cancellation.studentID, cancellation.student || cancellation.studentName);
                if (entry) entry.actual += 1;
            });
        });

        const uniqueEntries = [];
        const seen = new Set();
        studentMap.forEach((entry) => {
            if (!seen.has(entry)) {
                seen.add(entry);
                uniqueEntries.push(entry);
            }
        });
        return uniqueEntries;
    }

    const calculateTimesheetPercentageForTeacher = (teacher) => {
        if (!teacher) return null
        const teacherId = teacher.TeacherID
        const teacherSheets = timesheets.filter((sheet) => isSameId(sheet.teacherId, teacherId))
        if (!teacherSheets.length) return null

        const aggregate = new Map()

        teacherSheets.forEach((sheet) => {
            const families = Array.isArray(sheet.families) ? sheet.families : []
            const stats = collectStudentLessonStats(families, sheet.teacherId)
            stats.forEach((entry) => {
                const key = entry.primaryKey
                const existing = aggregate.get(key) || { expected: entry.expected || 4, actual: 0 }
                existing.actual += entry.actual
                aggregate.set(key, existing)
            })
        })

        if (!aggregate.size) {
            if (Array.isArray(teacher.newStudentList) && teacher.newStudentList.length) {
                return 0
            }
            return null
        }

        const totalExpected = Array.from(aggregate.values()).reduce((sum, entry) => sum + (entry.expected || 4), 0)
        if (!totalExpected) return null
        const totalActual = Array.from(aggregate.values()).reduce((sum, entry) => sum + Math.min(entry.actual, entry.expected || 4), 0)
        const pct = Math.round((totalActual / totalExpected) * 100)
        return pct
    }

    const calculateTimesheetPercentageForSheet = (sheet) => {
        if (!sheet) return null
        const families = Array.isArray(sheet.families) ? sheet.families : []
        const stats = collectStudentLessonStats(families, sheet.teacherId)
        if (!stats.length) return null
        const totalExpected = stats.reduce((sum, entry) => sum + (entry.expected || 4), 0)
        if (!totalExpected) return null
        const totalActual = stats.reduce((sum, entry) => sum + Math.min(entry.actual, entry.expected || 4), 0)
        const pct = Math.round((totalActual / totalExpected) * 100)
        return pct
    }

    const parseCurrency = (value) => {
        if (typeof value !== 'string') return 0
        const numeric = Number(value.replace(/[^0-9.-]+/g, ''))
        return Number.isNaN(numeric) ? 0 : numeric
    }

    const [sortKey, setSortKey] = React.useState('name')
    const [sortDirection, setSortDirection] = React.useState('asc')

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortKey(key)
            setSortDirection('asc')
        }
    }

    const sortIndicator = (key) => (sortKey === key ? (sortDirection === 'asc' ? '▲' : '▼') : '')

    const getSortedTeachersForSchool = (school) => {
        const rows = teachers
            .filter((teacher) => teacher.School === school)
            .map((teacher) => {
                const status = checkIfTheyTurnedInTimesheet(teacher.TeacherID)
                const multipleInfo = checkIfTheyTurnedInMultipleTimesheets(teacher.TeacherID)
                const rating = calculateTimesheetPercentageForTeacher(teacher)
                const amountText = ifTheyHaveTurnedInTheirTimesheetCalculatePay(teacher.TeacherID)
                const grayedOut = (Array.isArray(teacher.newStudentList) ? teacher.newStudentList.length : 0) === 0 && status === 'Missing'
                return {
                    teacher,
                    status,
                    multipleInfo,
                    name: teacher.Name || '',
                    firstName: ((teacher.Name || '').split(' ')[0] || '').toLowerCase(),
                    email: (teacher.Email || '').toLowerCase(),
                    studentsCount: Array.isArray(teacher.newStudentList) ? teacher.newStudentList.length : 0,
                    rating,
                    amountText,
                    amountValue: parseCurrency(amountText),
                    grayedOut
                }
            })

        rows.sort((a, b) => {
            if (a.grayedOut !== b.grayedOut) {
                return a.grayedOut ? 1 : -1;
            }
            const dir = sortDirection === 'asc' ? 1 : -1
            switch (sortKey) {
                case 'status':
                    return a.status.localeCompare(b.status) * dir
                case 'name': {
                    if (a.firstName !== b.firstName) return a.firstName.localeCompare(b.firstName) * dir
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase()) * dir
                }
                case 'email':
                    return a.email.localeCompare(b.email) * dir
                case 'students':
                    return (a.studentsCount - b.studentsCount) * dir
                case 'rating':
                    return ((a.rating ?? -1) - (b.rating ?? -1)) * dir
                case 'amount':
                    return (a.amountValue - b.amountValue) * dir
                default:
                    return 0
            }
        })

        return rows
    }

    const getSortedTimesheetsForSchool = (school) => {
        const rows = timesheets
            .filter((sheet) => sheet.school === school)
            .map((sheet) => {
                const rating = calculateTimesheetPercentageForSheet(sheet)
                const amountText = ifTheyHaveTurnedInTheirTimesheetCalculatePay(sheet.teacherId)
                return {
                    sheet,
                    status: sheet.status || '',
                    name: sheet.teacherName || '',
                    firstName: ((sheet.teacherName || '').split(' ')[0] || '').toLowerCase(),
                    email: (sheet.email || '').toLowerCase(),
                    studentsCount: 0,
                    rating,
                    amountText,
                    amountValue: parseCurrency(amountText),
                    grayedOut: false
                }
            })

        rows.sort((a, b) => {
            if (a.grayedOut !== b.grayedOut) {
                return a.grayedOut ? 1 : -1;
            }
            const dir = sortDirection === 'asc' ? 1 : -1
            switch (sortKey) {
                case 'status':
                    return a.status.localeCompare(b.status) * dir
                case 'name': {
                    if (a.firstName !== b.firstName) return a.firstName.localeCompare(b.firstName) * dir
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase()) * dir
                }
                case 'email':
                    return a.email.localeCompare(b.email) * dir
                case 'students':
                    return (a.studentsCount - b.studentsCount) * dir
                case 'rating':
                    return ((a.rating ?? -1) - (b.rating ?? -1)) * dir
                case 'amount':
                    return (a.amountValue - b.amountValue) * dir
                default:
                    return 0
            }
        })

        return rows
    }

    const renderCompletionBadge = (pct) => {
        if (pct == null) return '-'
        let level = 'good'
        if (pct < 60) level = 'bad-dark'
        else if (pct < 80) level = 'bad'
        return <span className={`timesheet-percent timesheet-percent--${level}`}>{pct}%</span>
    }

    return(
        <div>
        {schoolList.map((school) => {
            const sortedCurrentTeachers = getSortedTeachersForSchool(school)
            const sortedPastTimesheets = getSortedTimesheetsForSchool(school)
            return (
            <div className="whole-timesheet-wrapper" key={school}>
            {school === schoolTab &&
            <div>
                <div className="timesheet-school-name__title">
                    <div className="student-db-btn-wrapper on-timesheet-wrapper">
                        <div className="add-email-btn on-timesheet" onClick={() => copyAttempt()}>Copy Emails</div>
                    </div>
                </div>
                <table className="student-table">
                    <thead>
                        <tr>
                            <th><input onClick={(e) => massCheck(e.target.checked)} type="checkbox" style={{border: '1px solid black'}} /></th>
                            <th className="sortable-header" onClick={() => handleSort('status')}>
                                Status
                                <span className="sortable-header__indicator">{sortIndicator('status')}</span>
                            </th>
                            <th className="sortable-header" onClick={() => handleSort('name')}>
                                Name
                                <span className="sortable-header__indicator">{sortIndicator('name')}</span>
                            </th>
                            <th className="sortable-header" onClick={() => handleSort('email')}>
                                Email
                                <span className="sortable-header__indicator">{sortIndicator('email')}</span>
                            </th>
                            <th className="sortable-header" onClick={() => handleSort('students')}>
                                Students
                                <span className="sortable-header__indicator">{sortIndicator('students')}</span>
                            </th>
                            <th className="sortable-header" onClick={() => handleSort('rating')}>
                                Rating
                                <span className="sortable-header__indicator">{sortIndicator('rating')}</span>
                            </th>
                            <th className="sortable-header" onClick={() => handleSort('amount')}>
                                Amount Due
                                <span className="sortable-header__indicator">{sortIndicator('amount')}</span>
                            </th>
                        </tr>
                    </thead>
                    {currentMonth === month && currentYear === year ? 
                    <tbody>
                        {/* ---------------------------------CURRENT TIMESHEET LIST ------------------------------------------*/}
                        {sortedCurrentTeachers.map(({ teacher, status, multipleInfo, rating, amountText }) => (
                            <tr style={teacher.newStudentList.length === 0 && status === 'Missing' ? {opacity: '0.4'} : {opacity: 1}} key={teacher.TeacherID} className="each-row">
                                <td><input className={"student-checkbox"} style={{border: '1px solid black'}} type="checkbox" value={teacher.Email}/></td>
                                {status === 'Missing' ? (
                                    <td onClick={() => openUpATimesheet(teacher.TeacherID)} style={teacher.newStudentList.length > 0 ? {color: '#7e1111', backgroundColor: '#f5b3b3', fontWeight: 900, padding: '2px', borderRadius: '3px', fontSize: '12px', width: '100px', display: 'inline-block', margin: '8px auto', textAlign: 'center'} : {color: 'black'}}>{status}</td>
                                ) : status === 'Submitted' ? (
                                    <td onClick={() => openUpATimesheet(teacher.TeacherID)} style={{color: '#7e5611', backgroundColor: '#f5ddb3', fontWeight: 900, padding: '2px', borderRadius: '3px', fontSize: '12px', width: '100px', display: 'inline-block', margin: '8px auto', textAlign: 'center'}}>{status}</td>
                                ) : status === 'Approved' ? (
                                    <td onClick={() => openUpATimesheet(teacher.TeacherID)} style={{color: '#117e18', backgroundColor: '#bcf5b3', fontWeight: 900, padding: '2px', borderRadius: '3px', fontSize: '12px', width: '100px', display: 'inline-block', margin: '8px auto', textAlign: 'center'}}>{status}</td>
                                ) : (
                                    <td onClick={() => openUpATimesheet(teacher.TeacherID)}>{status || '-'}</td>
                                )}
                                <td onClick={() => openUpATimesheet(teacher.TeacherID)} >
                                    {teacher.Name}
                                    {multipleInfo.submitted > 1 ? (
                                        <div
                                            className='multiple-timesheets-warning'
                                            style={multipleInfo.approved === multipleInfo.submitted ? {color: 'green', border: '1px solid green'} : {color: 'orange', border: '1px solid orange'}}
                                        >
                                            {multipleInfo.approved}/{multipleInfo.submitted} timesheets
                                        </div>
                                    ) : null}
                                </td>
                                <td onClick={() => openUpATimesheet(teacher.TeacherID)} >{teacher.Email}</td>
                                <td onClick={() => openUpATimesheet(teacher.TeacherID)} >{teacher.newStudentList.length}</td>
                                <td onClick={() => openUpATimesheet(teacher.TeacherID)} >{renderCompletionBadge(rating)}</td>
                                <td onClick={() => openUpATimesheet(teacher.TeacherID)} >
                                    {amountText}
                                    {multipleInfo.submitted > 1 && <div className='multiple-timesheet-total'>Total</div>}
                                </td>
                                <td><MdInfoOutline onMouseEnter={() => findTimesheetFromTeacherId(teacher.TeacherID)} onMouseLeave={() => setQuickView(false)} /></td>
                            </tr>
                        ))}
                    </tbody>
                    :
                    // --------------------------------------------OUT OF DATE TIMESHEET LIST???------------------------------------------------
                    <tbody>
                        {sortedPastTimesheets.map(({ sheet, status, rating, amountText }) => (
                            <tr key={sheet.timesheetId} className="each-row">
                                <td><input className={"student-checkbox"} style={{border: '1px solid black'}} type="checkbox" value={sheet.email}/></td>
                                <td onClick={() => openUpATimesheet(sheet.teacherId)} >{sheet.teacherName}</td>
                                <td onClick={() => openUpATimesheet(sheet.teacherId)} >{sheet.email}</td>
                                <td onClick={() => openUpATimesheet(sheet.teacherId)} >-</td>
                                <td onClick={() => openUpATimesheet(sheet.teacherId)} >{renderCompletionBadge(rating)}</td>
                                {status === 'Submitted' ? (
                                    <td onClick={() => openUpATimesheet(sheet.teacherId)} style={{color: 'black'}}>{status}</td>
                                ) : status === 'Approved' ? (
                                    <td onClick={() => openUpATimesheet(sheet.teacherId)} style={{color: 'green'}}>{status}</td>
                                ) : (
                                    <td onClick={() => openUpATimesheet(sheet.teacherId)}>{status || '-'}</td>
                                )}
                                <td onClick={() => openUpATimesheet(sheet.teacherId)} >{amountText}</td>
                            </tr>
                        ))}
                    </tbody>
                    }
                </table>
            </div>
            }
        </div>
        )})}
    </div>  
    )
}
export default TimesheetsTab;
